import {
  FEEDBACK_CONSENT_LABEL_DOWNVOTE,
  FEEDBACK_CONSENT_LABEL_UPVOTE,
  type ProductFeedbackType,
} from '@littlebird/ui-utils';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { SegmentedControl } from '@/components/ui/segmented-control';
import TextArea from '@/components/ui/TextArea';
import { Tooltip } from '@/components/ui/Tooltip';
import { SnapshotDumpSelector } from '@/features/feedback';
import { useSendFeedbackReportCard } from '@/features/feedback-report/useSendFeedbackReportCard';
import { cn } from '@/lib/utils';

import type { ReactNode } from 'react';

const FEEDBACK_TYPE_OPTIONS: {
  value: ProductFeedbackType;
  label: string;
  icon: LBIcon;
}[] = [
  { value: 'bug', label: 'Bug', icon: Icons.debug },
  { value: 'feature', label: 'Feature request', icon: Icons.sparkle },
  { value: 'other', label: 'Other', icon: Icons.moreHorizontal },
];

// Grow the textarea with its content, clamped between 6 and 14 visible rows.
function feedbackRows(text: string): number {
  return Math.min(14, Math.max(6, text.split('\n').length + 1));
}

export interface SendFeedbackReportCardProps {
  draftId: string;
}

export function SendFeedbackReportCard({
  draftId,
}: SendFeedbackReportCardProps) {
  const {
    contentType,
    voteSentiment,
    sendState,
    draftQuery,
    loadErrorMessage,
    refetch,
    reportText,
    setReportText,
    includeChatHistory,
    setIncludeChatHistory,
    isCalendarRelated,
    includeCalendarEvents,
    setIncludeCalendarEvents,
    feedbackType,
    setFeedbackType,
    handleSend,
    snapshotDumpApps,
    selectedDumpApps,
    toggleDumpApp,
    isPreparingDumps,
    shouldShowDumpSelector,
  } = useSendFeedbackReportCard({ draftId });

  const isProductFeedback = contentType === 'product_feedback';
  const isSending = sendState === 'sending';

  if (sendState === 'sent') {
    return (
      <FeedbackCardShell>
        <CardHeader
          badge={<IconBadge icon={Icons.check} variant="success" />}
          title="Sent to the team"
          subtitle="We read every report"
        />
      </FeedbackCardShell>
    );
  }

  if (draftQuery.isError) {
    return (
      <FeedbackCardShell>
        <span className="flex items-center gap-3 px-4 py-3">
          <IconBadge icon={Icons.info} variant="muted" />
          <span className="flex-1 min-w-0 text-sm text-muted-foreground truncate">
            {loadErrorMessage ?? 'Couldn’t load this feedback draft'}
          </span>
          <Tooltip label="Retry" side="top">
            <button
              onClick={() => refetch()}
              aria-label="Retry"
              className="shrink-0 flex items-center justify-center size-7 rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <Icons.refresh className="size-4" />
            </button>
          </Tooltip>
        </span>
      </FeedbackCardShell>
    );
  }

  const checkboxId = `include-chat-history-${draftId}`;
  const calendarCheckboxId = `include-calendar-events-${draftId}`;

  return (
    <FeedbackCardShell maxWidth="lg" className="overflow-hidden">
      <CardHeader
        badge={<IconBadge icon={Icons.feedback} variant="muted" />}
        title="Send feedback"
        subtitle="We review all feedback to improve your experience with Littlebird"
      />

      {draftQuery.isPending ? (
        <span className="block px-4 py-3 text-xs text-muted-foreground">
          Loading…
        </span>
      ) : (
        <>
          {isProductFeedback && (
            <span className="block px-4 pt-3">
              <FeedbackTypeSelector
                value={feedbackType}
                onChange={setFeedbackType}
                disabled={isSending}
              />
            </span>
          )}

          <span className="block px-4 py-3">
            <TextArea
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              disabled={isSending}
              rows={feedbackRows(reportText)}
              className="min-h-32 text-sm leading-relaxed resize-y"
              placeholder="Describe the issue…"
              aria-label="Feedback report"
            />
          </span>

          {shouldShowDumpSelector && (
            <SnapshotDumpSelector
              apps={snapshotDumpApps}
              selected={selectedDumpApps}
              onToggle={toggleDumpApp}
              disabled={isSending}
              isPreparing={isPreparingDumps}
              className="px-4 pb-3"
            />
          )}

          {isCalendarRelated && (
            <span className="flex items-center gap-2.5 px-4 pb-3">
              <Checkbox
                id={calendarCheckboxId}
                checked={includeCalendarEvents}
                onCheckedChange={(checked) =>
                  setIncludeCalendarEvents(checked === true)
                }
                disabled={isSending}
              />
              <label
                htmlFor={calendarCheckboxId}
                className="flex-1 min-w-0 cursor-pointer select-none text-xs font-medium leading-snug text-foreground"
              >
                Include my calendar events (titles, times and attendees) to help
                debug this
              </label>
            </span>
          )}

          <span
            className={cn(
              'flex items-center gap-2.5 px-4 py-2.5',
              isProductFeedback && 'justify-end'
            )}
          >
            {!isProductFeedback && (
              <>
                <Checkbox
                  id={checkboxId}
                  checked={includeChatHistory}
                  onCheckedChange={(checked) =>
                    setIncludeChatHistory(checked === true)
                  }
                  disabled={isSending}
                />
                <label
                  htmlFor={checkboxId}
                  className="flex-1 min-w-0 cursor-pointer select-none"
                >
                  <span className="block text-xs font-medium leading-snug text-foreground">
                    {voteSentiment === 'positive'
                      ? FEEDBACK_CONSENT_LABEL_UPVOTE
                      : FEEDBACK_CONSENT_LABEL_DOWNVOTE}
                  </span>
                </label>
              </>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={handleSend}
              disabled={isSending || !reportText.trim()}
            >
              {isSending ? (
                <Icons.loader className="size-3 animate-spin" />
              ) : (
                <Icons.send className="size-3" />
              )}
              {isSending ? 'Sending…' : 'Send'}
            </Button>
          </span>
        </>
      )}
    </FeedbackCardShell>
  );
}

function FeedbackCardShell({
  maxWidth = 'md',
  className,
  children,
}: {
  maxWidth?: 'md' | 'lg';
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'block w-full my-3',
        maxWidth === 'lg' ? 'max-w-lg' : 'max-w-md'
      )}
    >
      <span
        className={cn(
          'block rounded-lg border border-border bg-background',
          className
        )}
      >
        {children}
      </span>
    </span>
  );
}

function IconBadge({
  icon: Icon,
  variant,
}: {
  icon: LBIcon;
  variant: 'muted' | 'success';
}) {
  return (
    <span
      className={cn(
        'flex items-center justify-center shrink-0 size-7 rounded-md bg-muted text-muted-foreground',
        variant === 'success' && 'rounded-full bg-green-500/10 text-green-500'
      )}
    >
      <Icon className={cn('size-4', variant === 'success' && 'size-3.5')} />
    </span>
  );
}

function CardHeader({
  badge,
  title,
  subtitle,
}: {
  badge: ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <span className="flex items-center gap-3 px-4 py-3">
      {badge}
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-foreground">
          {title}
        </span>
        <span className="block text-xs text-muted-foreground">{subtitle}</span>
      </span>
    </span>
  );
}

function FeedbackTypeSelector({
  value,
  onChange,
  disabled,
}: {
  value: ProductFeedbackType;
  onChange: (value: ProductFeedbackType) => void;
  disabled: boolean;
}) {
  return (
    <SegmentedControl.Root
      asChild
      value={value}
      onValueChange={(next) => onChange(next as ProductFeedbackType)}
    >
      <span className="block">
        <SegmentedControl.List asChild className="w-full">
          <span>
            {FEEDBACK_TYPE_OPTIONS.map(
              ({ value: optionValue, label, icon: Icon }) => (
                <SegmentedControl.Trigger
                  key={optionValue}
                  value={optionValue}
                  disabled={disabled}
                  className="flex-1 gap-1.5"
                >
                  <Icon className="size-3.5" />
                  {label}
                </SegmentedControl.Trigger>
              )
            )}
          </span>
        </SegmentedControl.List>
      </span>
    </SegmentedControl.Root>
  );
}
